// Tilts the camera for the strategic view: straight down in strategic mode, back to the automatic
// pitch in 3D. The camera has no pitch tween that leaves flights alone, so this steps its pose each
// frame while it runs (jump() sets the pitch and keeps the rest), and afterwards only repairs a pitch
// that something else reset (a shot's setPose, say).
//
//   3d ─ '=' ─► pitch eases to STRATEGIC_PITCH over TILT_S, held there (sticky override)
//   strategic ─ '=' ─► pitch eases to the automatic pitch, then the override is cleared

import type { ViewMode } from '../api.ts';
import { pitchFor, type CivCamera } from '../camera.ts';
import { approach, ease } from './tween.ts';

export const STRATEGIC_PITCH = 90;
const TILT_S = 0.3;
/** A held pitch this far off its goal is put back. */
const HOLD_TOLERANCE = 0.5;

export interface CameraDrive {
  set(mode: ViewMode): void;
  update(dt: number): void;
}

/** Pitch `t` (0..1, eased) of the way from `from` to `to`. */
export function pitchAt(from: number, to: number, t: number): number {
  return from + (to - from) * ease(t);
}

export function createCameraDrive(camera: CivCamera): CameraDrive {
  let mode: ViewMode = '3d';
  let from = 0;
  let t = 1;

  function goal(): number {
    return mode === 'strategic' ? STRATEGIC_PITCH : pitchFor(camera.pose().height);
  }

  return {
    set(next) {
      if (next === mode) {
        return;
      }
      mode = next;
      from = camera.pose().pitch;
      t = 0;
    },
    update(dt) {
      if (t < 1) {
        t = approach(t, 1, dt, TILT_S);
        camera.jump({ pitch: pitchAt(from, goal(), t) });
        if (t >= 1 && mode === '3d') {
          camera.jump({ pitch: null });
        }
        return;
      }

      if (mode === 'strategic' && Math.abs(camera.pose().pitch - STRATEGIC_PITCH) > HOLD_TOLERANCE) {
        camera.jump({ pitch: STRATEGIC_PITCH });
      }
    },
  };
}
